import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { AboutInfo } from '../../components/Site/AboutSection';
import useMoney from '../../hooks/useMoney';
import LangEnum from '../../lang/LangEnum';
import { SignatureInfo, team2Signature } from '../../lib/SiteLib';
import { SimpleSocialMedia, appendSocialMedia } from '../../lib/SocialMedias';
import { brandShowroomRepository } from '../../services/modules/brand-showroom/brand-showroom.repository';
import {
  BrandShowroom,
  BrandShowroomAbout,
  BrandShowroomContact,
  ShowroomViewMode,
} from '../../services/modules/brand-showroom/brand-showroom.types';
import { AveragePriceOption, WholesaleMediumPricesTypeEnum } from '../../types/Enums';
import { empty2Null } from '../../utils/formatters';
import { useApplicationContext } from '../context/ApplicationContext/ApplicationContext';
import { Team } from '../context/ApplicationContext/applicationContext.types';
import { useAuthContext } from '../login/AuthContext';
import { exploreRepository } from '../my-network/explore/explore.repository';
import { Explore } from '../my-network/explore/myNetworkExplore.types';
import { useTranslation } from '../translation/TranslationContext';
import { useAbility } from '../users/AbilityContext';
import { userBrandPositions } from '../users/roles';
import { validateShowroomPageEdit } from './brandShowroom.validator';

const repository = brandShowroomRepository();
const exploreRepo = exploreRepository();

type Tags = 'error' | 'success';

type State = {
  aboutData: Partial<AboutInfo>;
  signaturesData: SignatureInfo[];
  brandShowroom?: BrandShowroom;
  exploreConnection?: Explore;
  loading: boolean;
  tag?: Tags;
  editingAboutSection: boolean;
  canEditShowroom: boolean;
  canShowEditButton: boolean;
  viewMode: ShowroomViewMode;
};

type BrandShowroomProps = State & {
  load: (slug: string) => Promise<any>;
  saveAbout: (values: BrandShowroomAbout) => Promise<boolean | undefined>;
  refreshExploreConnection: () => void;
  onSaveContact: (values: SignatureInfo) => Promise<boolean>;
  closeEditingSection: () => void;
  afterEditShowroom: () => void;
  openEditingSection: () => void;
  toggleViewMode: () => void;
  setViewMode: Dispatch<SetStateAction<ShowroomViewMode>>;
};

const useBrandShowroom = (brandId: string): BrandShowroomProps => {
  const [editingAboutSection, setEditingAboutSection] = useState<boolean>(false);
  const [brandShowroom, setBrandShowroom] = useState<BrandShowroom | undefined>();
  const [aboutData, setAboutData] = useState<Partial<AboutInfo>>({});
  const [signaturesData, setSignaturesData] = useState<SignatureInfo[]>([]);
  const [exploreConnection, setExploreConnection] = useState<Explore | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [tag, setTag] = useState<Tags>();
  const [viewMode, setViewMode] = useState<ShowroomViewMode>('VISUALIZATION');
  const ability = useAbility();

  const { translate, translateOptions } = useTranslation();
  const { formatMoney } = useMoney();

  const { isAdminBrand, team } = useApplicationContext();
  const { loggedin } = useAuthContext();
  const canEnableEditMode = isAdminBrand || ability.can('update', 'showroom');

  useEffect(() => {
    setViewMode(canEnableEditMode ? 'EDITION' : 'VISUALIZATION');
  }, [canEnableEditMode]);

  const buildExploreConnection = useCallback(async (currentBrand: BrandShowroom) => {
    if (!loggedin) {
      return;
    }

    setExploreConnection(undefined);
    const exploreResponse = await exploreRepo.exploreBrand(currentBrand.id);
    if (exploreResponse.ok) {
      setExploreConnection(exploreResponse.data);
    }
  }, []);

  const load = useCallback(
    async (brandIdToLoad?: string) => {
      if (!brandIdToLoad) {
        setBrandShowroom(undefined);
        return;
      }
      setLoading(true);

      const { ok: brandSuccess, data: brandData } = await repository.get(brandIdToLoad);

      if (brandSuccess) {
        setBrandShowroom(brandData);
        setTag('success');
      } else {
        setTag('error');
      }

      setLoading(false);
    },
    [setBrandShowroom, setLoading, setTag],
  );

  const reload = () => {
    load(brandId);
  };

  const saveContact = async (signature: SignatureInfo) => {
    const values: BrandShowroomContact = {
      email: empty2Null(signature.email),
      countryCode: empty2Null(signature.countryCode),
      phone: empty2Null(signature.phone),
      mainDepartment: empty2Null(signature.role),
    };

    const saveResponse = await repository.saveContact(values);
    if (saveResponse.ok) {
      reload();
    }

    return saveResponse.ok;
  };

  const afterEditShowroom = () => {
    setEditingAboutSection(false);
    reload();
  };

  const handleSaveContact = async (values: SignatureInfo) => {
    const isSaved = await saveContact(values);
    afterEditShowroom();
    return isSaved;
  };

  const loadContacts = async (strId: string): Promise<Team[]> => {
    if (!loggedin) {
      return [];
    }

    const response = await repository.getContacts(strId);
    if (response.ok && response.data?.data) {
      return response.data?.data;
    }

    return [];
  };

  const writeWholesaleMediumPrice = useCallback(
    (mediumPrice: string) => {
      const WholesaleMediumPricesTypeEnumValues =
        WholesaleMediumPricesTypeEnum.values as AveragePriceOption[];

      const mediumPriceObject = WholesaleMediumPricesTypeEnumValues.find(
        (item) => item.value === mediumPrice,
      );

      return mediumPriceObject
        ? translate(mediumPriceObject.label as LangEnum, {
            price: formatMoney(mediumPriceObject.lowerValue),
            priceFrom: formatMoney(mediumPriceObject.lowerValue),
            priceTo: formatMoney(mediumPriceObject.biggerValue!),
          })
        : undefined;
    },
    [formatMoney, translate],
  );

  const buildAboutSection = useCallback(
    (currentBrand: BrandShowroom) => {
      const medias: SimpleSocialMedia[] = [];

      appendSocialMedia('FACEBOOK', currentBrand.facebookUrl, medias);
      appendSocialMedia('GOOGLEPLUS', currentBrand.googlePlusUrl, medias);
      appendSocialMedia('INSTAGRAM', currentBrand.instagramUrl, medias);
      appendSocialMedia('YOUTUBE', currentBrand.youtubeUrl, medias);
      appendSocialMedia('PINTEREST', currentBrand.pinterestUrl, medias);
      appendSocialMedia('TWITTER', currentBrand.twitterUrl, medias);

      const abInfo = {
        name: currentBrand?.commercialName,
        slogan: currentBrand.title,
        site: currentBrand.website,
        about: currentBrand.aboutMe,
        wholesaleMediumPrice: currentBrand.wholesaleMediumPrice,
        mediumPrices: currentBrand.wholesaleMediumPrice
          ? writeWholesaleMediumPrice(currentBrand.wholesaleMediumPrice)
          : undefined,
        medias,
        segments: currentBrand.segments,
        logoUrl: currentBrand.logoUrl,
      };

      setAboutData(abInfo);
    },
    [writeWholesaleMediumPrice],
  );

  const buildSignatureSection = useCallback(
    async (currentBrand: BrandShowroom) => {
      const mainSignature: SignatureInfo = {
        name: currentBrand.commercialName,
        email: currentBrand.email,
        countryCode: currentBrand.countryCode,
        phone: currentBrand.phone,
        role: currentBrand.mainDepartment,
        avatar: currentBrand.logoUrl,
        editable: true,
      };

      const contacts = await loadContacts(currentBrand.id);
      const newContacts = contacts.map((contact) =>
        team2Signature(contact, false, translateOptions(userBrandPositions)),
      );

      setSignaturesData([mainSignature, ...newContacts]);
    },
    [translateOptions],
  );

  useEffect(() => {
    if (!brandShowroom) {
      setAboutData({});
      return;
    }

    buildAboutSection(brandShowroom);
    buildSignatureSection(brandShowroom);
    buildExploreConnection(brandShowroom);
  }, [brandShowroom, buildSignatureSection, buildExploreConnection, buildAboutSection]);

  const refreshExploreConnection = useCallback(() => {
    if (!brandShowroom) {
      setExploreConnection(undefined);
      return;
    }

    buildExploreConnection(brandShowroom);
  }, [buildExploreConnection, brandShowroom]);

  const saveAbout = async (values: BrandShowroomAbout) => {
    const saveResponse = await repository.saveAbout(values);
    if (saveResponse.ok) {
      reload();
    }

    return saveResponse.ok;
  };

  const canShowEditButton = validateShowroomPageEdit({
    hasPermissionToEdit: isAdminBrand || ability.can('update', 'showroom'),
    brandShowroomId: brandShowroom?.id,
    teamBrandId: team?.brand?.id,
  });
  const canEditShowroom = viewMode === 'EDITION' && canShowEditButton;

  const toggleViewMode = () => {
    setViewMode((view) => (view === 'EDITION' ? 'VISUALIZATION' : 'EDITION'));
  };

  return {
    aboutData,
    signaturesData,
    brandShowroom,
    tag,
    exploreConnection,
    loading,
    editingAboutSection,
    viewMode,
    canEditShowroom,
    canShowEditButton,
    load,
    saveAbout,
    afterEditShowroom,
    onSaveContact: handleSaveContact,
    refreshExploreConnection,
    closeEditingSection: () => setEditingAboutSection(false),
    openEditingSection: () => setEditingAboutSection(true),
    toggleViewMode,
    setViewMode,
  };
};

export default useBrandShowroom;
